import { METHODS, HttpClientError, EventType } from "@dpdgroupuk/fetch-client";

class ElectronFetchClient {
  constructor({
    headers,
    electronApisClient,
    ...config
  }) {
    this.options = {
      debug: process.env.NODE_ENV === "development",
      parse: response => response,
      electronApisClient,
      ...config,
      baseUrl: window.location.origin
    };
    this.subscribers = [];
  }

  on(type, fn) {
    this.subscribers[type] = [...(this.subscribers[type] || []), fn];
  }

  off(type, fn) {
    this.subscribers[type] = this.subscribers[type].filter(subscriber => subscriber !== fn);
  }

  _notify(type, event) {
    const listeners = this.subscribers[type] || [];
    listeners.forEach(subscriber => {
      subscriber(event);
    });
  }

  _handleResponse(res, requestOptions) {
    const jsonResult = res.body;

    if (res.status >= 400) {
      if (this.options.debug) {
        // eslint-disable-next-line
        console.warn("Request with parameters", requestOptions, "has been failed with status", res.status, "and response", jsonResult);
      }

      throw HttpClientError.parse(res, jsonResult);
    }

    return this.options.parse(jsonResult);
  }

  async fetch({
    method,
    path,
    query,
    body,
    options: {
      signal
    } = {}
  }) {
    const {
      baseUrl,
      electronApisClient,
      parse,
      ...defaultOptions
    } = this.options;
    const requestOptions = {
      url: `${baseUrl}${path}`,
      path,
      query,
      body,
      method,
      ...defaultOptions
    };

    const fetchFunction = async () => {
      return electronApisClient(requestOptions).then(res => {
        if (signal?.aborted) {
          throw new Error("Aborted by user");
        }

        return this._handleResponse(res, requestOptions);
      }).then(response => {
        this._notify(EventType.Success, response);

        return response;
      });
    };

    return fetchFunction().catch(e => {
      if (requestOptions.onRetryError) {
        return requestOptions.onRetryError.bind(this)(fetchFunction, e).catch(e => {
          this._notify(EventType.Error, e);

          throw e;
        });
      }

      this._notify(EventType.Error, e);

      throw e;
    });
  }

  get(params) {
    if (typeof params === "string") {
      params = {
        path: params
      };
    }

    return this.fetch({ ...params,
      method: METHODS.GET
    });
  }

  post(params) {
    return this.fetch({ ...params,
      method: METHODS.POST
    });
  }

  put(params) {
    return this.fetch({ ...params,
      method: METHODS.PUT
    });
  }

  delete(params) {
    return this.fetch({ ...params,
      method: METHODS.DELETE
    });
  }

  sendFormData(params) {
    return this.fetch({ ...params,
      method: METHODS.POST,
      isFormData: true
    });
  }

}

export default ElectronFetchClient;